'use client'

import * as React from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { StarIcon } from 'primitives/icons/star'
import { tw } from 'utils/classnames'

export function Review({
  image,
  rating,
  title,
  date,
  copy,
}: {
  image: string
  rating: number
  title: string
  date: Date
  copy: string
}) {
  const t = useTranslations('pages.promo.yearly.reviews')
  const [showAllText, setShowAllText] = React.useState(false)
  const [showReadMoreButton, setShowReadMoreButton] = React.useState(false)

  const copyElement = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    function shouldShowReadMoreButton(element: HTMLDivElement) {
      return element.offsetHeight < element.scrollHeight
    }

    if (copyElement.current) {
      setShowReadMoreButton(shouldShowReadMoreButton(copyElement.current))
    }
  }, [])

  return (
    <div className="bg-cf-white relative flex flex-col border border-[#e3e1e9] px-5 pb-[14px] pt-[18px]">
      <div className="flex flex-col md:flex-row md:justify-between">
        <div className="flex flex-row gap-[10px]">
          <Image src={image} width={40} height={40} alt={title} className="rounded-full" />
          <div>
            <h3 className="mb-[2px] font-bold">{title}</h3>
            <p className="text-xs text-[#6E7E82]">
              {date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>
        </div>
        <div className="mt-5 flex font-bold md:mt-0">
          <span className="mr-[6px] text-[16px]">{rating}</span>
          {Array(5)
            .fill(true)
            .map((_, index) => (
              <StarIcon
                key={index}
                className={tw(
                  'm-[-3px] inline-block size-7',
                  index < rating ? 'text-[#FFB400]' : 'text-gray-300',
                )}
                checked={index < rating}
              />
            ))}
        </div>
      </div>
      <div>
        <p
          ref={copyElement}
          className={tw('pt-5 text-sm md:pl-[50px] md:pt-[17px]', !showAllText && 'line-clamp-2')}
        >
          {copy}
        </p>
        {!showAllText ? (
          <button
            className={tw(
              "float-right p-[10px] text-sm text-[#4929a0] after:absolute after:-inset-3 after:content-['']",
              !showReadMoreButton && 'pointer-events-none opacity-0',
            )}
            onClick={() => setShowAllText(true)}
          >
            {t('read_more')}
          </button>
        ) : null}
      </div>
    </div>
  )
}
